import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import { UserCheck, UserX } from 'lucide-react';
import * as React from 'react';

import { NotPermitted, ReasonDialog } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { accountsApi } from '../api/accounts-api';
import type { AccountStatus, IAdminAccountRow } from '../api/account-contracts';
import { NOT_REREAD, isNotFound, unanswered } from '../pages/person-hub/said';
import { neverArrived, statusRefusal } from '../refusal';

/**
 * An account's status on the user card (SR-421, over survey-api's SR-442): staff disable an
 * account, and make it active again — `PUT /api/v1/admin/accounts/{accountId}/status`.
 *
 * **A press in the page's header, not a card of its own** (the owner's ask of 2026-10-20): it acts
 * on the whole account, so it sits with the page's title, and the state it leaves is said at the
 * top of the page (`AccountStatusNotice`) rather than in a card further down. One press, named
 * for the move the status takes: "Disable account…" on an active account, "Activate account…"
 * on a disabled one. A status this app does not know is offered the disablement.
 *
 * **Drawn off, never hidden, with the reason said under it**: the caller's own account
 * (survey-api's `own-account`, D3), a caller without `accounts.manage`, and a Team Portal account
 * for a caller without `roles.manage` (D2, which survey-api answers with a bodiless 403). Whether
 * the account holds a permission the caller lacks is survey-api's to judge (D5): the press is
 * offered, and `permission-not-held` is said.
 *
 * **A confirmed press with an optional reason** (`ReasonDialog`, its 500 characters said and
 * never enforced), whose words say what the 204 does not: disabling ends every session and stops
 * every way in, and nothing the account did is removed; reactivating restores no session, and
 * the owner signs in through Invent as before. A blank reason is left out of the body.
 *
 * **What comes back**: a landing re-reads the page and is said at the top (`onSaid`, with
 * `NOT_REREAD` where the re-read failed — the page then still offers the move just made, which
 * the write's idempotence makes harmless); a refusal — a 400, `own-account`,
 * `permission-not-held`, the policy's 403 — is said in the dialog, the reason kept; an answer that
 * never arrived closes the dialog, is said at the top and re-reads; a 404 closes it and re-reads,
 * and the page then says "No such account".
 */

/** What the status write was told, said at the top of the user card. Keyed by the account, as the
 *  page is not remounted when it follows another one. */
export interface IAccountStatusSaid {
  accountId: string;
  tone: 'done' | 'refused';
  text: string;
}

/** survey-api's bound on the reason (`AccountStatusService.MaxReasonLength`). */
const MAX_REASON = 500;

/** The bodiless refusal the dialog says: the policy's 403 — `accounts.manage` gone, or a Team
 *  Portal account and `roles.manage` gone (D2). A 404 is the page's (`confirm`). */
const STATUS_REFUSALS: Record<number, string> = {
  403:
    'The system refused this — your Team Portal roles do not include managing accounts, or, for ' +
    'a Team Portal account, managing roles. The page is read again with your current roles.'
};

/** Each move in words: the press, the dialog and what a landing says. */
const MOVES: Record<
  AccountStatus,
  { press: string; title: (email: string) => string; confirm: string; landed: string }
> = {
  disabled: {
    press: 'Disable account…',
    title: (email) => `Disable ${email}?`,
    confirm: 'Disable the account',
    landed: 'Disabled. Every session the account held has ended.'
  },
  active: {
    press: 'Activate account…',
    title: (email) => `Activate ${email}?`,
    confirm: 'Activate the account',
    landed: 'Activated. Its owner signs in again as before.'
  }
};

/** The move a press makes on an account in this status. */
function moveOf(status: string): AccountStatus {
  return status === 'disabled' ? 'active' : 'disabled';
}

/** A refusal the dialog says, the reason kept — told by type first, then by status; a 400's
 *  `problems[]` are said through `statusRefusal`'s fall-through to `refusalMessage`. */
function statusWriteRefusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isProblem(ProblemType.ownAccount)) {
      return 'The system refused this: it is your own account. Ask another administrator.';
    }
    if (error.isProblem(ProblemType.permissionNotHeld)) {
      return (
        'The system refused this: the account holds a permission your Team Portal roles do not, ' +
        'so its status is for someone holding at least as much. Nothing was changed.'
      );
    }
  }
  return statusRefusal(error, STATUS_REFUSALS, 'Could not change the status');
}

export function AccountStatusPress({
  account,
  isSelf,
  canManage,
  canGrant,
  onChanged,
  onSaid
}: {
  account: IAdminAccountRow;
  isSelf: boolean;
  /** `accounts.manage` — the route's policy. */
  canManage: boolean;
  /** `roles.manage` — asked besides for a Team Portal account (D2). */
  canGrant: boolean;
  /** The page's re-read — `false` where it failed. */
  onChanged: () => Promise<boolean>;
  onSaid: (said: IAccountStatusSaid) => void;
}) {
  const next = moveOf(account.status);
  // The move is fixed as the dialog opens: the re-read a landing makes flips the account's status
  // while the dialog is still on its way out, and its title must not turn into the other move's.
  const [move, setMove] = React.useState<AccountStatus>(next);
  const [open, setOpen] = React.useState(false);
  const reasonId = React.useId();

  const asked = MOVES[move];
  let offRule: Nullable<React.ReactNode> = null;
  if (isSelf) {
    offRule = (
      <p id={reasonId} data-own-account className="text-sm text-muted-foreground">
        This is your own account — the system refuses it.
      </p>
    );
  } else if (!canManage) {
    offRule = <NotPermitted id={reasonId} what="disabling or activating an account" />;
  } else if (account.kind === 'staff' && !canGrant) {
    offRule = (
      <NotPermitted
        id={reasonId}
        what="managing roles, which a Team Portal account’s status asks as well"
      />
    );
  }

  const say = (tone: IAccountStatusSaid['tone'], text: string) =>
    onSaid({ accountId: account.accountId, tone, text });

  const confirm = async (reason: string): Promise<Nullable<string>> => {
    try {
      await accountsApi.setStatus(account.accountId, {
        status: move,
        ...(reason === '' ? {} : { reason })
      });
    } catch (error) {
      if (unanswered(error)) {
        say(
          'refused',
          neverArrived(
            'the change of status',
            'The account is read again — check its status before pressing again.'
          )
        );
        void onChanged();
        return null;
      }
      if (isNotFound(error)) {
        // The account is gone: the re-read draws the page's "No such account".
        void onChanged();
        return null;
      }
      return statusWriteRefusal(error);
    }
    const reread = await onChanged();
    say('done', asked.landed + (reread ? '' : NOT_REREAD));
    return null;
  };

  const Icon = next === 'disabled' ? UserX : UserCheck;

  return (
    <div className="grid justify-items-end gap-1" data-account-status-press>
      <Button
        type="button"
        size="sm"
        variant={next === 'disabled' ? 'outline' : 'default'}
        className="gap-1.5"
        disabled={offRule !== null}
        aria-describedby={offRule === null ? undefined : reasonId}
        onClick={() => {
          setMove(next);
          setOpen(true);
        }}
      >
        <Icon className="size-4" aria-hidden />
        {MOVES[next].press}
      </Button>
      {offRule && <div className="max-w-xs text-right">{offRule}</div>}

      <ReasonDialog
        open={open}
        onOpenChange={setOpen}
        title={asked.title(account.email)}
        description={
          move === 'disabled' ? (
            <>
              Nobody can sign in as this account until it is activated again. Every session it holds
              ends now, and no link mailed for it — an invitation to sign in, an address
              confirmation — works while it is disabled. Nothing it did is removed: its edits,
              decisions and notes stay under its name.
            </>
          ) : (
            <>
              Its owner signs in again as before. The sessions that ended when it was disabled do
              not come back, and a link of its that has not expired works again.
            </>
          )
        }
        required={false}
        max={MAX_REASON}
        confirmLabel={asked.confirm}
        onConfirm={confirm}
      >
        <p className="text-xs text-muted-foreground">
          The reason is kept with the change in the account’s history.
        </p>
      </ReasonDialog>
    </div>
  );
}

/**
 * What the top of the user card says about the status: a disabled account in red — the one fact
 * that outranks every other on the page, since nothing else on it lets anybody in — and what the
 * last press was told. Nothing is said of an active account but that.
 */
export function AccountStatusNotice({
  account,
  said
}: {
  account: IAdminAccountRow;
  said: Nullable<IAccountStatusSaid>;
}) {
  const shown = said?.accountId === account.accountId ? said : null;
  return (
    <>
      {account.status === 'disabled' && (
        <Alert variant="destructive" data-account-disabled>
          <UserX className="size-4" aria-hidden />
          <AlertTitle>This account is disabled — it has no way into the system</AlertTitle>
          <AlertDescription>
            Nobody can sign in as it, no session of it works, and no link mailed for it — an
            invitation to sign in, an address confirmation — works until it is activated again.
            Nothing it did was removed. Activate it to let its owner sign in again as before.
          </AlertDescription>
        </Alert>
      )}
      {shown && (
        <p
          role={shown.tone === 'refused' ? 'alert' : 'status'}
          data-account-status-said
          className={
            shown.tone === 'refused' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'
          }
        >
          {shown.text}
        </p>
      )}
    </>
  );
}
